import type { IEventBus } from "@/domains/events"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { ILockLookup } from "@/domains/lock/interfaces"
import type { ITokenLookup } from "@/domains/tokens/types"
import { OrchestratedQuery } from "@/domains/library"

import type {
  IEmissionsConfig,
  IGaugeApiTransport,
  IGaugeConnectorStrategy,
  IGaugeOnChainTransport,
  IGaugeRepository,
} from "./interfaces"
import type { GaugeApiData } from "./types"
import { GaugeOrchestrator } from "./gauge-orchestrator"

export class GaugeOrchestratorFactory {
  /**
   * Create a fully configured GaugeOrchestrator with its OrchestratedQuery
   */
  static create(
    gaugeRepository: IGaugeRepository,
    gaugeApiTransport: IGaugeApiTransport,
    gaugeOnChainTransport: IGaugeOnChainTransport,
    orchestratedQueryStore: OrchestratedQueryStore,
    eventBus: IEventBus,
    connectorStrategies: IGaugeConnectorStrategy[],
    emissionsConfig: IEmissionsConfig | undefined,
    tokenLookup: ITokenLookup,
    lockLookup: ILockLookup,
  ): GaugeOrchestrator {
    // 1. Create orchestrator with all dependencies
    const orchestrator = new GaugeOrchestrator(
      gaugeRepository,
      gaugeApiTransport,
      gaugeOnChainTransport,
      eventBus,
      connectorStrategies,
      emissionsConfig,
      tokenLookup,
      lockLookup,
    )

    // 2. Create gauge bribes sync query with 5-minute interval (300000ms)
    // Reduced from 1 minute since we're using real-time event listening
    const loadGaugeBribesQuery = new OrchestratedQuery<Record<string, GaugeApiData>>(
      "Gauge Bribes Sync",
      (query) => orchestrator.onApiLoadGaugeBribesTrigger(query),
      300_000, // 5 minutes
      orchestrator.onGaugeBribesQuerySuccess.bind(orchestrator),
      orchestrator.onGaugeBribesQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
    )

    // 3. Initialize orchestrator with query
    // The orchestrator will start the query immediately in initialize()
    // since gauges are public data and should be available even without wallet connection
    orchestrator.initialize(loadGaugeBribesQuery)

    return orchestrator
  }
}
